import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import {
  listarTiposSolicitaDebMunicipioCadastro,
  inserirTipoSolicitaDebMunicipio,
  excluirTipoSolicitaDebMunicipio,
} from '~/Services/Service';

const schema = Yup.object().shape({
  assunto: Yup.string() // V-20
    .required('Descrição obrigatória')
    .max(100, 'Máximo 100 caracteres'),
});

const TiposSolicitacaoModal = ({ show, onHide }) => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const [ordenacao, setOrdenacao] = useState({ campo: 'assunto', direcao: 'asc' });

  useEffect(() => {
    if (show) {
      carregar();
    }
  }, [show]);

  const carregar = async () => {
    setLoading(true);
    try {
      const resp = await listarTiposSolicitaDebMunicipioCadastro();
      setTipos(resp || []);
    } catch (err) {
      toast.error('Erro ao carregar tipos de solicitação.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const tiposOrdenados = [...tipos].sort((a, b) => {
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const va = a[ordenacao.campo];
    const vb = b[ordenacao.campo];
    if (va == null) return 1 * dir;
    if (vb == null) return -1 * dir;
    if (typeof va === 'string') return va.localeCompare(vb) * dir;
    return (va - vb) * dir;
  });

  const handleInserir = async (values, formikBag) => {
    try {
      await inserirTipoSolicitaDebMunicipio({ assunto: values.assunto });
      toast.success('Tipo de solicitação criado com sucesso.');
      setShowForm(false);
      carregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirTipoSolicitaDebMunicipio(item.codigo);
      toast.success('Tipo excluído com sucesso.');
      carregar();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
            Tipos de Solicitação
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="text-right mb-2">
            <Button variant="success" onClick={() => setShowForm((prev) => !prev)}>
              <FaPlus className="mr-1" /> Novo Tipo
            </Button>
          </div>

          {/* Formulário de inclusão */}
          {showForm && (
            <Formik
              initialValues={{ assunto: '' }}
              validationSchema={schema}
              onSubmit={handleInserir}
            >
              {(formik) => (
                <Form onSubmit={formik.handleSubmit}>
                  <Row className="align-items-end mb-3 border rounded p-2 bg-light mx-0">
                    <Col md={9}>
                      <Form.Group className="mb-0">
                        <Form.Label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>
                          Descrição *
                        </Form.Label>
                        <Form.Control
                          name="assunto"
                          value={formik.values.assunto}
                          onChange={formik.handleChange}
                          isInvalid={formik.touched.assunto && !!formik.errors.assunto}
                          placeholder="Ex: SOLICITAÇÃO DE DÉBITOS - IPTU"
                          autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.assunto}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3} className="text-right">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="mr-2"
                      >
                        {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setShowForm(false)}
                      >
                        Cancelar
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          )}

          {loading ? (
            <div className="py-3"><Loading /></div>
          ) : (
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleOrdenacao('codigo')}
                  >
                    Código {iconeOrdenacao('codigo')}
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleOrdenacao('assunto')}
                  >
                    Descrição {iconeOrdenacao('assunto')}
                  </th>
                  <th style={{ width: 80 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tiposOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-3">
                      Nenhum tipo cadastrado.
                    </td>
                  </tr>
                ) : (
                  tiposOrdenados.map((tipo) => (
                    <tr key={tipo.codigo}>
                      <td>{tipo.codigo}</td>
                      <td>{tipo.assunto}</td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Excluir"
                          onClick={() => handleExcluir(tipo)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Excluir Tipo"
        message={
          confirmacao.item
            ? `Confirma a exclusão do tipo "${confirmacao.item.assunto}"?`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />
    </>
  );
};

export default TiposSolicitacaoModal;
